import { toast } from "$global-providers/toast-provider";
import {
  Box,
  Button,
  CircularProgress,
  styled,
  Typography,
} from "@material-ui/core";
import type { EpocusRole } from "elephant-web/gql";
import { phone } from "phone";
import { Navigate, useNavigate } from "react-router-dom";
import {
  selectActiveProgramId,
  useMyPrograms,
} from "../../../_lib/use-active-program";
import { linkToEpocusManageUsers, linkToEpocusSettings } from "../../../links";
import { useCanManageProgramUsers } from "../../_lib/use-can-manage-program-users";
import { useEpocusCreateUserMutation } from "../_lib/__generated__/program-users-operations.gql";
import {
  ProgramUserForm,
  type ProgramUserFormValues,
} from "../_lib/program-user-form";

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 600,
  margin: "0 auto",
});

export const EpocusAddUserPage: FC = () => {
  const canManageProgramUsers = useCanManageProgramUsers();
  const { programs, loading: programsLoading } = useMyPrograms();
  const programId = selectActiveProgramId(programs);
  const navigate = useNavigate();

  const [createUser, { loading }] = useEpocusCreateUserMutation({
    onCompleted: () => {
      toast.success(
        "User created — they'll get an email to set their password",
      );
      navigate(linkToEpocusManageUsers());
    },
    onError: (e) => toast.error(`Couldn't create user: ${e.message}`),
  });

  if (!canManageProgramUsers) {
    return <Navigate to={linkToEpocusSettings()} replace />;
  }

  const handleSubmit = async (values: ProgramUserFormValues) => {
    if (!programId) return;

    await createUser({
      variables: {
        input: {
          programId,
          role: values.role as EpocusRole,
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          telephone: values.telephone
            ? phone(values.telephone).phoneNumber || values.telephone
            : undefined,
        },
      },
      refetchQueries: ["EpocusProgramUsers"],
    });
  };

  return (
    <PageRoot>
      <Button
        onClick={() => navigate(linkToEpocusManageUsers())}
        style={{
          textTransform: "none",
          padding: "4px 0",
          minWidth: 0,
          alignSelf: "flex-start",
        }}
      >
        ← Back to users
      </Button>

      <Typography variant="h5" style={{ fontWeight: 700 }}>
        Add user
      </Typography>

      {programsLoading ? (
        <Box display="flex" justifyContent="center" padding={3}>
          <CircularProgress aria-label="Loading your program" />
        </Box>
      ) : !programId ? (
        <Typography>Couldn't determine your program.</Typography>
      ) : (
        <ProgramUserForm
          submitLabel="Create user"
          submitting={loading}
          emailEditable
          onSubmit={handleSubmit}
        />
      )}
    </PageRoot>
  );
};
